/**
 * Topbar — ORION R11 shell
 * Sticky bovenbalk met breadcrumb (paginacontext), meldingen-bel en
 * bedrijf-chip (tenantnaam + dropdown). Aanwezig op elk scherm via Layout.
 */
import { useLocation, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Bell, ChevronDown, Building2, User as UserIcon, LogOut, Square, PanelRight, SplitSquareHorizontal } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useNavPanelStore, DEFAULT_SCREEN_STATE } from '../stores/navPanelStore'
import { useGlobalShortcuts } from '../hooks/useGlobalShortcuts'
import api from '../services/api'

const NAV_MODES = [
  { m: 1, Icon: Square, label: 'Enkel scherm' },
  { m: 2, Icon: PanelRight, label: 'Contextueel zijpaneel' },
  { m: 3, Icon: SplitSquareHorizontal, label: 'Splitscreen' },
]

// Bovenste pad-segment -> nette label voor de breadcrumb
const ROUTE_LABELS = {
  dashboard: 'Dashboard',
  projecten: 'Projecten',
  'projecten-overzicht': 'Projecten',
  'projecten-dashboard': 'Projecten',
  offertes: 'Offertes',
  snelofferte: 'Snelofferte',
  klanten: 'Klanten',
  materialen: 'Materialen',
  leveranciers: 'Leveranciers',
  facturen: 'Facturen',
  bestellingen: 'Bestellingen',
  productie: 'Productie',
  planning: 'Planning',
  rapporten: 'Rapporten',
  instellingen: 'Instellingen',
  voorraad: 'Voorraad',
  inventory: 'Voorraad',
  crm: 'CRM',
  ruimtes: 'Ruimtes',
  documenten: 'Documenten',
  werkbonnen: 'Werkbonnen',
  admin: 'Beheer',
}

function titleCase(s) {
  return String(s || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function Topbar({ schermtype }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, logout } = useAuthStore()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  // --- Drie-lagen navigatie: mode-schakelaar + ⌘J ---
  const screenKey = schermtype || (location.pathname.split('/').filter(Boolean)[0] || 'dashboard')
  const screens = useNavPanelStore((s) => s.screens)
  const setMode = useNavPanelStore((s) => s.setMode)
  const setPanelOpen = useNavPanelStore((s) => s.setPanelOpen)
  const { mode, panelOpen } = { ...DEFAULT_SCREEN_STATE, ...(screens[screenKey] || {}) }
  const panelActief = panelOpen && mode >= 2
  const actieveModus = panelActief ? mode : 1
  const togglePanel = () => {
    if (panelActief) setPanelOpen(screenKey, false)
    else setMode(screenKey, mode >= 2 ? mode : 2) // openen (setMode zet panelOpen)
  }
  useGlobalShortcuts([
    { key: 'j', meta: true, preventDefault: true, id: 'nav-panel-toggle', handler: togglePanel },
  ])

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Bedrijfsnaam (graceful fallback bij 401/500)
  const { data: tenant } = useQuery({
    queryKey: ['topbar-tenant', user?.id],
    queryFn: async () => {
      try {
        const r = await api.get('/tenant/profiel')
        return r.data
      } catch {
        return null
      }
    },
    enabled: !!user?.id,
    retry: false,
    staleTime: 300000,
  })
  const companyName = tenant?.naam || t('common.orionSaaS', { defaultValue: 'Orion' })

  // Breadcrumb uit het pad
  const segs = location.pathname.split('/').filter(Boolean)
  const seg0 = segs[0] || 'dashboard'
  const primary = ROUTE_LABELS[seg0] || titleCase(seg0)
  const secondary = seg0 === 'dashboard'
    ? t('dashboard.managementDashboard', { defaultValue: 'Mission Control' })
    : null

  const initials = String(user?.gebruikersnaam || 'U')
    .split(/[\s.@]+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()

  return (
    <header className="sticky top-0 z-20 h-16 bg-surface/95 dark:bg-gray-800/95 backdrop-blur border-b border-line dark:border-gray-700 flex items-center justify-between px-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm min-w-0">
        <span className="font-semibold text-ink dark:text-white truncate">{primary}</span>
        {secondary && (
          <>
            <span className="text-muted">/</span>
            <span className="text-muted truncate">{secondary}</span>
          </>
        )}
      </div>

      {/* Acties rechts */}
      <div className="flex items-center gap-3">
        {/* Navigatie-mode schakelaar (enkel / zijpaneel / splitscreen) — ⌘J togglet het paneel */}
        <div className="flex items-center gap-0.5 p-0.5 rounded-control border border-line dark:border-gray-700">
          {NAV_MODES.map(({ m, Icon, label }) => {
            const actief = actieveModus === m
            return (
              <button
                key={m}
                onClick={() => setMode(screenKey, m)}
                title={label}
                aria-pressed={actief}
                className={`p-1.5 rounded transition-colors ${actief ? 'bg-accent/10 text-accent' : 'text-muted hover:text-ink dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <Icon className="w-4 h-4" />
              </button>
            )
          })}
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="relative p-2 rounded-control text-muted hover:text-ink dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          title={t('common.notifications', { defaultValue: 'Meldingen' })}
        >
          <Bell className="w-5 h-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-600 rounded-full" />
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-control border border-line dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Building2 className="w-4 h-4 text-muted" />
            <span className="text-sm font-medium text-ink dark:text-white max-w-[160px] truncate">{companyName}</span>
            <ChevronDown className={`w-4 h-4 text-muted transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-surface dark:bg-gray-800 rounded-card border border-line dark:border-gray-700 shadow-lg py-2 z-30">
              <div className="px-4 py-2 border-b border-line dark:border-gray-700">
                <p className="text-sm font-semibold text-ink dark:text-white truncate">{companyName}</p>
                <p className="text-xs text-muted truncate flex items-center gap-1 mt-0.5">
                  <UserIcon className="w-3 h-3" />
                  {user?.gebruikersnaam || ''} {user?.rol ? `· ${user.rol}` : ''}
                </p>
              </div>
              <button
                onClick={() => { setMenuOpen(false); logout?.() }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('layout.user.logout', { defaultValue: 'Uitloggen' })}
              </button>
            </div>
          )}
        </div>

        {/* Initialen-avatar */}
        <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold select-none" title={user?.gebruikersnaam || ''}>
          {initials}
        </div>
      </div>
    </header>
  )
}

export default Topbar
